import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchLeagueView, getUserData, saveMatchResult, fetchH2H, fetchPaymentDebtStatus } from '../../api/api';
import LeagueCountdown from './LeagueCountdown';
import LeagueGroupTable from './LeagueGroupTable';
import MatchesTable from '../CompetitionSystems/MatchesTable/MatchesTable';
import MatchResultModal from '../CompetitionSystems/MatchResultModal/MatchResultModal';
import H2HModal from '../CompetitionSystems/H2HModal/H2HModal';
import CustomModal from '../../UI/Modal/CustomModal';
import Spinner from '../Spinner/Spinner';
import RecentResultsDrawer from './RecentResultsDrawer';
import ScheduledMatchesDrawer from './ScheduledMatchesDrawer';
import styles from './LeagueView.module.css';

const LeagueView = ({ tournament, isArchived, onReturn }) => {
    const navigate = useNavigate();
    const [leagueData, setLeagueData] = useState(null);
    const [selectedRound, setSelectedRound] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [userData, setUserData] = useState({});
    const [editingMatchId, setEditingMatchId] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isCustomModalOpen, setIsCustomModalOpen] = useState(false);
    const [modalMessage, setModalMessage] = useState('');
    const [isH2hModalOpen, setIsH2hModalOpen] = useState(false);
    const [h2hMatch, setH2hMatch] = useState(null);
    const [h2hData, setH2hData] = useState([]);
    const [isH2hLoading, setIsH2hLoading] = useState(false);
    const [isTransitioning, setIsTransitioning] = useState(false);
    const [selectedGroup, setSelectedGroup] = useState(null);
    const [redirectToLoginAfterModal, setRedirectToLoginAfterModal] = useState(false);
    const [isRecentDrawerOpen, setIsRecentDrawerOpen] = useState(false);
    const [isScheduledDrawerOpen, setIsScheduledDrawerOpen] = useState(false);
    const [nameFilter, setNameFilter] = useState('');
    const [isDebtModalOpen, setIsDebtModalOpen] = useState(false);

    // Bramka „Planowanie meczów": przy zaległościach pokaż komunikat zamiast nawigować.
    const handleMatchPlanningClick = async () => {
        try {
            const status = await fetchPaymentDebtStatus();
            if (status?.hasDebt) {
                setIsDebtModalOpen(true);
                return;
            }
        } catch (e) {
            // Błąd sprawdzenia nie blokuje (egzekwowanie zaległości i tak następuje
            // przy próbie rezerwacji kortu) — pozwalamy przejść dalej.
            console.error('Nie udało się sprawdzić zaległości płatniczych:', e);
        }
        navigate(`/leagues/${tournament.id}/match-planning`);
    };

    useEffect(() => {
        const fetchUser = async () => {
            const data = await getUserData();
            setUserData(data);
        };
        fetchUser();
    }, []);

    const loadData = async (roundNum) => {
        setIsLoading(true);
        const result = await fetchLeagueView(tournament.id, roundNum);
        if (result.success) {
            setLeagueData(result.data);
            if (selectedRound === null) {
                setSelectedRound(result.data.currentRoundNumber);
            }
        }
        setIsLoading(false);
    };

    // Ciche odświeżenie w tle — w odróżnieniu od loadData NIE rusza isLoading, bo przy
    // isLoading komponent zwraca <Spinner /> zamiast treści i zdjąłby z ekranu widok
    // razem z otwartym modalem wyniku. Błąd sieci zostawia poprzednie dane.
    const refreshSilently = async () => {
        const result = await fetchLeagueView(tournament.id, selectedRound);
        if (result.success) {
            setLeagueData(result.data);
        }
    };

    useEffect(() => {
        loadData(selectedRound);
    }, [tournament.id, selectedRound]);

    // Wyniki turnieju rozgrywanego na żywo mają pojawiać się same, bez F5.
    useEffect(() => {
        if (isArchived) return; // turniej zamknięty — wyniki finalne

        const intervalId = setInterval(() => {
            if (isModalOpen) return;      // ktoś wpisuje wynik — nie odświeżamy mu pod ręką
            if (isTransitioning) return;  // trwa już poll przejścia rundy (co 5 s)
            if (document.hidden) return;  // karta w tle — nie budzimy telefonu
            refreshSilently();
        }, 30000);

        return () => clearInterval(intervalId);
    }, [tournament.id, selectedRound, isArchived, isModalOpen, isTransitioning]);

    const handleRoundChange = (e) => {
        setSelectedRound(parseInt(e.target.value));
    };

    const transitionIntervalRef = useRef(null);

    const handleRoundExpired = () => {
        if (isTransitioning) return;

        // Ostatnia runda — backend nie przejdzie do kolejnej, więc nie czekamy na rotację.
        // Flaga liczona po stronie serwera (LeagueService.isFinalRound), żeby front nie
        // powielał formuły okna rundy i się z nią nie rozjeżdżał.
        if (leagueData.finalRound) {
            setModalMessage('Liga zakończona — ostatnia runda dobiegła końca.');
            setIsCustomModalOpen(true);
            return;
        }

        setIsTransitioning(true);
        const expiredRound = leagueData?.currentRoundNumber;

        transitionIntervalRef.current = setInterval(async () => {
            const result = await fetchLeagueView(tournament.id, null);
            if (result.success && result.data.currentRoundNumber !== expiredRound) {
                clearInterval(transitionIntervalRef.current);
                setLeagueData(result.data);
                setSelectedRound(result.data.currentRoundNumber);
                setIsTransitioning(false);
            }
        }, 5000);
    };

    useEffect(() => {
        return () => {
            if (transitionIntervalRef.current) clearInterval(transitionIntervalRef.current);
        };
    }, []);

    const handleEditMatch = (matchId) => {
        setEditingMatchId(matchId);
        setIsModalOpen(true);
    };

    const onSaveResult = async (matchId, scores, walkover, walkoverWinnerId, injuryResignation, injuryResignationParticipantId, clearMatchData) => {
        const matchResult = {
            matchId, scores, walkover, walkoverWinnerId,
            injuryResignation, injuryResignationParticipantId, clearMatchData
        };
        const result = await saveMatchResult(matchResult);
        setIsModalOpen(false);
        if (result.success) {
            setModalMessage('Wynik zapisany pomyślnie');
            loadData(selectedRound);
        } else {
            setModalMessage('Błąd zapisu wyniku: ' + (result.data || ''));
            if (result.sessionExpired) {
                setRedirectToLoginAfterModal(true);
            }
        }
        setIsCustomModalOpen(true);
    };

    const handleH2H = async (matchId) => {
        const allM = leagueData.groupStandings.flatMap(g => g.matches);
        const match = allM.find(m => m.id === matchId);
        setH2hMatch(match || null);
        setIsH2hLoading(true);
        setIsH2hModalOpen(true);
        const result = await fetchH2H(matchId);
        if (Array.isArray(result)) {
            setH2hData(result);
        }
        setIsH2hLoading(false);
    };


    if (isLoading || !leagueData) return <Spinner />;

    const allMatches = leagueData.groupStandings.flatMap(g => g.matches);
    const editingMatch = allMatches.find(m => m.id === editingMatchId);
    const userRoles = new Set(userData.authorities || []);
    const isSupervisor = userRoles.has("ROLE_SUPERVISOR") || userRoles.has("ROLE_ADMIN");
    const isLeagueAdmin = userRoles.has("ROLE_ADMIN");

    const totalMatches = allMatches.length;
    const playedMatches = allMatches.filter(m => m.participantWinnerId !== null).length;
    const progressPercentage = totalMatches > 0 ? Math.round((playedMatches / totalMatches) * 100) : 0;
    const isParticipant = leagueData.freezesRemaining !== null && leagueData.freezesRemaining !== undefined;
    const isViewingPastRound = selectedRound !== leagueData.currentRoundNumber;
    // Turnieje towarzyskie są nakładką na silnik ligi: jedna runda, brak klasyfikacji
    // wieloetapowej. Ranking między rundami i wybór rundy nie mają tu sensu.
    // `=== true`, bo turnieje sprzed wprowadzenia flagi mają `friendly = null`.
    const isFriendly = tournament.friendly === true;
    const displayedGroups = selectedGroup
        ? leagueData.groupStandings.filter(g => g.groupLabel === selectedGroup)
        : leagueData.groupStandings;

    // Filtrowanie po nazwisku/imieniu uczestnika lub partnera deblowego (case-insensitive, substring).
    // Gdy aktywny — grupy bez pasujących meczów są ukrywane (mniej szumu wizualnego).
    const nameQuery = nameFilter.trim().toLowerCase();
    const filteredGroups = !nameQuery
        ? displayedGroups
        : displayedGroups
            .map(g => ({
                ...g,
                matches: g.matches.filter(m => {
                    const names = [
                        m.participant1LastName, m.participant1FirstName,
                        m.participant2LastName, m.participant2FirstName,
                        m.participant1PartnerLastName, m.participant1PartnerFirstName,
                        m.participant2PartnerLastName, m.participant2PartnerFirstName,
                    ];
                    return names.some(n => n && n.toLowerCase().includes(nameQuery));
                }),
            }))
            .filter(g => g.matches.length > 0);

    return (
        <div className={styles.container}>
            {isTransitioning && (
                <div className={styles.transitionOverlay}>
                    <div className={styles.transitionContent}>
                        <p>Przejście do następnej rundy...</p>
                        <Spinner size="large" />
                    </div>
                </div>
            )}
            {isLeagueAdmin && !isArchived && (
                <button
                    className={styles.adminButton}
                    onClick={() => navigate(`/admin-league/${tournament.id}`)}
                >
                    Panel admina ligi
                </button>
            )}
            <div className={styles.roundHeader}>
                {!isFriendly && (
                    <button
                        className={styles.rankingLink}
                        onClick={() => navigate(`/leagues/${tournament.id}/ranking`, {
                            state: {
                                returnPath: '/tournaments',
                                returnState: {
                                    activeTab: isArchived ? 'archived' : 'current',
                                    tournamentId: tournament.id
                                }
                            }
                        })}
                    >
                        <span role="img" aria-label="ranking">🏆</span> Ranking i wyróżnienia
                    </button>
                )}

                {!isFriendly && (
                    <div className={styles.roundSelector}>
                        <span className={styles.roundLabel}>Runda:</span>
                        <select className={styles.roundSelect} value={selectedRound || ''} onChange={handleRoundChange}>
                            {leagueData.rounds.map(r => (
                                <option key={r.roundNumber} value={r.roundNumber}>
                                    {r.roundNumber}
                                </option>
                            ))}
                        </select>
                    </div>
                )}

{!isArchived && leagueData.roundEndDate && selectedRound === leagueData.currentRoundNumber && leagueData.config.countdownEnabled !== false && (
                    <LeagueCountdown
                        startDate={leagueData.config.currentRoundStartDate}
                        endDate={leagueData.roundEndDate}
                        onExpired={handleRoundExpired}
                    />
                )}

                {/* Pasek postępu i szufladki — tylko dla bieżącej rundy. Po przełączeniu
                    na rundę archiwalną nie mają sensu (runda zamknięta). */}
                {!isViewingPastRound && (
                    <>
                        {totalMatches > 0 && leagueData.groupStandings.length > 1 && (
                            <div className={styles.progressWrapper}>
                                <div className={styles.progressBar}>
                                    <div
                                        className={styles.progressFill}
                                        style={{
                                            width: `${progressPercentage}%`,
                                            backgroundColor: progressPercentage === 100 ? '#2196f3'
                                                : progressPercentage >= 67 ? '#4caf50'
                                                : progressPercentage >= 34 ? '#ffeb3b'
                                                : '#f44336',
                                        }}
                                    />
                                </div>
                                <div className={styles.progressText}>
                                    {progressPercentage}% rozegranych ({playedMatches}/{totalMatches})
                                </div>
                            </div>
                        )}

                        {/* Obie szufladki to funkcje wieloetapowej ligi. Wyłączone planowanie
                            meczów oznacza turniej jednodniowy (np. debel losowany) — wtedy
                            „Zaplanowane mecze" i tak nie ma skąd wziąć danych, bo czyta
                            zaakceptowane propozycje z planowania. */}
                        {leagueData.config.matchPlanningEnabled !== false && (
                            <>
                                <button
                                    type="button"
                                    className={styles.recentResultsLink}
                                    onClick={() => setIsRecentDrawerOpen(true)}
                                >
                                    🕒 Ostatnie wyniki
                                </button>

                                <button
                                    type="button"
                                    className={styles.recentResultsLink}
                                    onClick={() => setIsScheduledDrawerOpen(true)}
                                >
                                    📅 Zaplanowane mecze
                                </button>
                            </>
                        )}
                    </>
                )}

                {leagueData.groupStandings.length > 1 && (
                    <div className={styles.groupFilterRow}>
                        <button
                            className={`${styles.groupFilterButton} ${selectedGroup === null ? styles.groupFilterActive : ''}`}
                            onClick={() => setSelectedGroup(null)}
                        >
                            Wszystkie
                        </button>
                        {leagueData.groupStandings.map(g => (
                            <button
                                key={g.groupLabel}
                                className={`${styles.groupFilterButton} ${selectedGroup === g.groupLabel ? styles.groupFilterActive : ''}`}
                                onClick={() => setSelectedGroup(g.groupLabel)}
                            >
                                {g.groupLabel}
                            </button>
                        ))}
                    </div>
                )}
            </div>

            {isParticipant && !isArchived && !isViewingPastRound && leagueData.config.matchPlanningEnabled !== false && (
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 15 }}>
                    <button
                        className={styles.matchPlanningButton}
                        onClick={handleMatchPlanningClick}
                    >
                        🎾 Planowanie meczów
                    </button>
                </div>
            )}

            <div className={styles.nameFilterRow}>
                <div className={styles.nameFilterField}>
                    <svg
                        className={styles.nameFilterIcon}
                        viewBox="0 0 24 24"
                        width="16"
                        height="16"
                        aria-hidden="true"
                        focusable="false"
                    >
                        <circle cx="11" cy="11" r="7" fill="none" stroke="currentColor" strokeWidth="2"/>
                        <line x1="16.5" y1="16.5" x2="21" y2="21" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                    <input
                        type="text"
                        className={styles.nameFilterInput}
                        value={nameFilter}
                        onChange={(e) => setNameFilter(e.target.value)}
                        placeholder="Filtruj po nazwisku…"
                        aria-label="Filtruj po nazwisku"
                        autoComplete="off"
                        spellCheck={false}
                    />
                    {nameFilter && (
                        <button
                            type="button"
                            className={styles.nameFilterClear}
                            onClick={() => setNameFilter('')}
                            aria-label="Wyczyść filtr"
                        >
                            ×
                        </button>
                    )}
                </div>
            </div>

            {nameQuery && filteredGroups.length === 0 && (
                <div className={styles.nameFilterEmpty}>
                    Brak meczów pasujących do wyszukiwania
                </div>
            )}

            <div className={styles.groupsGrid}>
                {filteredGroups.map((group) => (
                    <div key={group.groupLabel} className={styles.groupSection}>
                        {leagueData.groupStandings.length > 1 && (
                            <h3 className={styles.groupTitle}>
                                Grupa {group.groupLabel}
                                <span className={styles.groupWeight}> (premia grupowa: {group.groupPremium ?? 0})</span>
                            </h3>
                        )}
                        <LeagueGroupTable
                            standings={group.standings}
                            promoted={group.effectivePromoted}
                            relegated={group.effectiveRelegated}
                            compact={leagueData.groupStandings.length === 1}
                        />
                        <MatchesTable
                            matches={group.matches}
                            onEditMatch={handleEditMatch}
                            onOpenH2H={handleH2H}
                            isArchived={isArchived || isViewingPastRound}
                            showBallProvider={isParticipant && !isViewingPastRound && !isArchived && leagueData.config.matchPlanningEnabled !== false}
                        />
                    </div>
                ))}
            </div>

            {isModalOpen && editingMatch && (
                <MatchResultModal
                    isOpen={isModalOpen}
                    onRequestClose={() => { setIsModalOpen(false); setEditingMatchId(null); }}
                    onSaveResult={onSaveResult}
                    editingMatch={editingMatch}
                    matchId={editingMatchId}
                    participant1Id={editingMatch.participant1Id}
                    participant2Id={editingMatch.participant2Id}
                    participant1FirstName={editingMatch.participant1FirstName}
                    participant1LastName={editingMatch.participant1LastName}
                    participant2FirstName={editingMatch.participant2FirstName}
                    participant2LastName={editingMatch.participant2LastName}
                    participant1PartnerFirstName={editingMatch.participant1PartnerFirstName}
                    participant1PartnerLastName={editingMatch.participant1PartnerLastName}
                    participant2PartnerFirstName={editingMatch.participant2PartnerFirstName}
                    participant2PartnerLastName={editingMatch.participant2PartnerLastName}
                    isThirdSetTiebreak={tournament.isThirdSetTiebreak}
                    matchFormat={tournament.matchFormat}
                    numberOfPointsInSet={tournament.numberOfPointsInSet}
                    isUserSupervision={isSupervisor}
                />
            )}

            <CustomModal
                isOpen={isCustomModalOpen}
                onRequestClose={() => {
                    setIsCustomModalOpen(false);
                    if (redirectToLoginAfterModal) {
                        // Hard redirect — pełne przeładowanie odświeża AuthContext (navbar zmienia „Wyloguj" → „Zaloguj się").
                        window.location.href = "/login?from=" + encodeURIComponent(window.location.pathname);
                    }
                }}
                message={modalMessage}
                isPositiveMessage={modalMessage.includes("pomyślnie") || modalMessage.includes("zrobione") || modalMessage.includes("Zamrożenie") || modalMessage.includes("odwołane")}
                cancelLabel="Zamknij"
                cancelButtonColor="green"
                showConfirmButton={false}
            />

            <CustomModal
                isOpen={isDebtModalOpen}
                onRequestClose={() => setIsDebtModalOpen(false)}
                message="Masz zaległe płatności! Sprawdź w zakładce 'Płatności'."
                isWarningMessage={true}
                showConfirmButton={false}
                cancelLabel="OK"
                onCancel={() => { setIsDebtModalOpen(false); navigate('/my-profile/payments'); }}
            />


            {isH2hModalOpen && (
                <H2HModal
                    isOpen={isH2hModalOpen}
                    onRequestClose={() => { setIsH2hModalOpen(false); setH2hMatch(null); setH2hData([]); }}
                    match={h2hMatch}
                    h2hData={h2hData}
                    isLoading={isH2hLoading}
                />
            )}

            <RecentResultsDrawer
                open={isRecentDrawerOpen}
                onClose={() => setIsRecentDrawerOpen(false)}
                tournamentId={tournament.id}
            />

            <ScheduledMatchesDrawer
                open={isScheduledDrawerOpen}
                onClose={() => setIsScheduledDrawerOpen(false)}
                tournamentId={tournament.id}
            />
        </div>
    );
};

export default LeagueView;
